import { IonContent, IonPage, IonSpinner } from '@ionic/react';
import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import TidalService from '../../services/Tidal.service';
import { useDispatch } from '../../store/hooks';
import { AppIdentifiers, setToken } from '../../store/slices/token-state.slice';
import isElectron from '../../utils/isElectron';
import normalizeHashRedirect from '../../utils/normalizeHashRedirect';
import './callback.scss';

const TidalCallback: React.FC = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const [error, setError] = useState<string | null>(null);
  const [needsManualEntry, setNeedsManualEntry] = useState(false);
  const [authCode, setAuthCode] = useState<string | null>(null);

  useEffect(() => {
    const handleCallback = async () => {
      // Check for tokens in URL (from auth bridge server-side exchange)
      const urlParams = new URLSearchParams(window.location.search);
      const hashParams = new URLSearchParams(
        window.location.hash.split('?')[1] || '',
      );
      const tokensParam = urlParams.get('tokens') || hashParams.get('tokens');

      if (tokensParam) {
        console.log('[Tidal Callback] Received tokens from auth bridge');
        try {
          const tokens = JSON.parse(atob(tokensParam));
          console.log('[Tidal Callback] Tokens decoded successfully');

          if (tokens.accessToken) {
            dispatch(
              setToken(
                AppIdentifiers.Tidal,
                tokens.accessToken,
                tokens.refreshToken,
                Date.now() + Number(tokens.expiresIn) * 1000,
              ),
            );

            // Also store in localStorage for TidalService
            localStorage.setItem('tidal_access_token', tokens.accessToken);
            localStorage.setItem('tidal_refresh_token', tokens.refreshToken);
            localStorage.setItem(
              'tidal_token_expiry',
              String(Date.now() + tokens.expiresIn * 1000),
            );

            const redirect = normalizeHashRedirect(
              localStorage.getItem('tidal_redirect'),
            );
            localStorage.removeItem('tidal_redirect');
            localStorage.removeItem('tidal_session_id');
            localStorage.removeItem('tidal_code_verifier');
            history.replace(redirect);
            return;
          }
        } catch (e) {
          console.error('[Tidal Callback] Failed to parse tokens:', e);
        }
      }

      // Fallback: check for auth code (traditional flow)
      const code = TidalService.getAuthCode();

      if (!code) {
        setError('No authorization code or tokens received');
        return;
      }

      // Check if we have the code verifier (needed for token exchange)
      const codeVerifier = localStorage.getItem('tidal_code_verifier');

      if (!codeVerifier) {
        console.log(
          '[Tidal Callback] No code verifier - showing manual entry option',
        );
        setAuthCode(code);

        if (!isElectron()) {
          setNeedsManualEntry(true);
          return;
        }
      }

      try {
        const tokenResponse = await TidalService.getToken(code);

        if (tokenResponse.accessToken) {
          dispatch(
            setToken(
              AppIdentifiers.Tidal,
              tokenResponse.accessToken,
              tokenResponse.refreshToken,
              Date.now() + Number(tokenResponse.expiresIn) * 1000,
            ),
          );

          const redirect = normalizeHashRedirect(
            localStorage.getItem('tidal_redirect'),
          );
          localStorage.removeItem('tidal_redirect');
          localStorage.removeItem('tidal_session_id');
          history.replace(redirect);
        } else {
          setError(
            tokenResponse.error_description ||
              tokenResponse.error ||
              'Failed to get access token',
          );
        }
      } catch (err: unknown) {
        console.error('Tidal callback error:', err);
        const errorMessage =
          err instanceof Error ? err.message : 'Unknown error';

        if (
          errorMessage.includes('code_verifier') ||
          errorMessage.includes('Missing')
        ) {
          setAuthCode(code);
          setNeedsManualEntry(true);
        } else {
          setError('Failed to authenticate with Tidal: ' + errorMessage);
        }
      }
    };

    handleCallback();
  }, [dispatch, history]);

  const copyCode = () => {
    if (authCode) {
      navigator.clipboard.writeText(authCode);
    }
  };

  return (
    <IonPage>
      <IonContent className="tidal-callback">
        <div className="callback-container">
          {error ? (
            <div className="callback-error">
              <h2>Authentication Failed</h2>
              <p>{error}</p>
              <button onClick={() => history.push('/pipelines')}>
                Back to Pipelines
              </button>
            </div>
          ) : needsManualEntry ? (
            <div className="callback-success">
              <div style={{ fontSize: '60px', marginBottom: '20px' }}>✅</div>
              <h2 style={{ color: '#00FFFF' }}>Tidal Connected!</h2>
              <p style={{ color: '#888', marginBottom: '20px' }}>
                Authentication successful. You can close this browser tab and
                return to the Crate Hackers desktop app.
              </p>
              <p style={{ color: '#666', fontSize: '14px' }}>
                The app should automatically detect your connection. If not,
                click Tidal again in the app.
              </p>
              {authCode && (
                <div style={{ marginTop: '20px' }}>
                  <button
                    onClick={copyCode}
                    style={{
                      background: '#00FFFF',
                      color: '#000',
                      border: 'none',
                      padding: '10px 20px',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontWeight: 600,
                    }}
                  >
                    Copy Auth Code
                  </button>
                  <p
                    style={{
                      color: '#666',
                      fontSize: '12px',
                      marginTop: '8px',
                    }}
                  >
                    If needed, paste this code in the app
                  </p>
                </div>
              )}
            </div>
          ) : (
            <div className="callback-loading">
              <IonSpinner name="crescent" />
              <p>Connecting to Tidal...</p>
            </div>
          )}
        </div>
      </IonContent>
    </IonPage>
  );
};

export default TidalCallback;
